import AurClientListView from '../../../client-management/aur-client-list-view/AurClientListView';
import UserDetailsView from '../../../user-profile/UserDetailsView';
import {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {Box, Button} from '@mui/material';
import {t} from 'i18next';
import AurButton from '../../../common/aur-button/AurButton';
import {useNavigate} from 'react-router-dom';
import {
  setUserId,
  setClientInfo,
  setProjectList,
} from '../../../../redux/slices/teamMemberSlice';
import {
  ADD_NEW_TEAM_MEMBER,
  BUDGET_ESTIMATED,
  EVIDENCE_SOURCE_MANAGEMENT,
  ONLY_DATE_SHOW_FORMAT,
} from '../../../../constants/constants';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import {labelProps} from '../../../user-management/aur-user-details-view/models';
import AurAccordionGroup from '../../../common/aur-accordion/AurAccordionGroup';
import {createUser} from '../../../../api/UserManagementService';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {selectUser} from '../../../../redux/slices/userSlice';
import {IClientDetail} from '../../../../types/client-management/cientManagementTypes';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {setShowModal} from '../../../../redux/slices/unSaveChangesModalSlice';
import AurSysAdminLabel from '../../../common/aur-sys-admin-label/AurSysAdminLabel';
import AurDialog from '../../../common/aur-dialog/AurDialog';
import {
  deleteUserRole,
  getUserRoles,
} from '../../../../api/ClientManagementService';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {UserRoleIds} from '../../../../types/user-management/userManagementTypes';
import {getPermissions} from '../../../../redux/slices/permissionsSlice';
import {getPermissionDetail} from './utils';
import {UserRolesData} from '../../../../redux/slices/userRoleSlice';
import {getUTCDateTimeForDisplay} from '../../../../utils/utils';
import {TeamMemberStatus} from '../models';
import {
  GA_CLICK_TYPES,
  GA_ERROR_CODES,
  GA_ERROR_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

type TeamMemberInfoProps = {
  activeStep: number;
  setActiveStep: (value: number) => void;
  timeStamp: string;
  setTimeStamp: (value: string) => void;
  resetForm: () => void;
};

// Review component will be display teamMemberInfo and Client Info before confirmation.
const Review = (props: TeamMemberInfoProps) => {
  const {activeStep, setActiveStep, timeStamp, setTimeStamp} = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {showToast} = useToast();

  const showModal = useSelector(
    (state: RootState) => state.unsavedChangesModal.showModal
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [userFullName, setUserFullName] = useState<string>('');
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const {trackGAEvent} = useAnalytics();

  const teamMemberInfo = useSelector(
    (state: RootState) => state.teamMember.teamMemberInfo
  );
  const clientInfo = useSelector(
    (state: RootState) => state.teamMember.clientInfo
  );
  const projectList = useSelector(
    (state: RootState) => state.teamMember.projectList
  );
  const [userListDisabled, setUserListDisabled] = useState<boolean>(false);
  const [userFirstName, setUserFirstName] = useState<string>(
    teamMemberInfo.firstName
  );
  const [userLastName, setUserLastName] = useState<string>(
    teamMemberInfo.lastName
  );

  const userEmail = teamMemberInfo.email;
  const [clientDetails, setClientDetails] =
    useState<IClientDetail>(getClientDetails());

  const user = useSelector(selectUser);
  const permissionsFromDB = useSelector(getPermissions) || [];

  useEffect(() => {
    if (user) {
      const fullname = `${user.givenname} ${user.surname}`;
      setUserFullName(fullname);
      setClientDetails((prevClientDetails) => ({
        ...prevClientDetails, // Retain previous clientDetails state
        updatedBy: fullname,
        createdBy: fullname,
      }));
    }
  }, [user]);

  useEffect(() => {
    if (showModal) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.global.unsavedChanges'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  }, [showModal]);

  useEffect(() => {
    dispatch(setShowModal(false));
    activeStep !== 3
      ? localStorage.setItem('currentPath', window.location.pathname)
      : localStorage.setItem('currentPath', '');

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    const handlePopState = () => {
      // Handle back button (popstate event)
      dispatch(setShowModal(true)); // Show custom modal
      window.history.pushState(null, '', window.location.pathname); // Prevent back navigation
    };

    // Add initial state to prevent back button navigation
    window.history.pushState(null, '', window.location.pathname);

    // Add event listeners for beforeunload and popstate
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (timeStamp) {
      setClientDetails((prevClientDetails) => ({
        ...prevClientDetails,
        createdDate: new Date(timeStamp),
        updatedDate: new Date(timeStamp),
      }));
    }
  }, [timeStamp]); // Re-run the effect when timeStamp changes

  const invitedStatusLabelProps: labelProps = {
    status: t('common.editDetails.invited'),
    icon: IconNames.Email,
    iconColor: IconColors.Success,
    iconSize: 'small',
  };

  const tempLabelDetails: labelProps[] = [
    {
      status: teamMemberInfo.isActive
        ? t('status.active')
        : t('status.inactive'),
      icon: teamMemberInfo.isActive ? IconNames.Circle : IconNames.CircleEmpty,
      iconColor: teamMemberInfo.isActive
        ? IconColors.Success
        : IconColors.Secondary,
      iconSize: 'small',
      ...(teamMemberInfo?.status === TeamMemberStatus.invited &&
      !teamMemberInfo.isActive
        ? invitedStatusLabelProps
        : {}),
    },
    {
      status: teamMemberInfo.isBillable
        ? t('common.userProfile.billable')
        : t('common.userProfile.nonBillable'),
      icon: teamMemberInfo.isBillable
        ? IconNames.CurrencyUSD
        : IconNames.CurrencyUSDClose,
      iconColor: teamMemberInfo.isBillable
        ? IconColors.Success
        : IconColors.Secondary,
      iconSize: 'small',
    },
    {
      status:
        teamMemberInfo?.isLimitIp &&
        teamMemberInfo?.ipAddressList &&
        teamMemberInfo?.ipAddressList.length > 0
          ? t('common.userProfile.limitedIPAccess')
          : '',
      icon:
        teamMemberInfo?.isLimitIp &&
        teamMemberInfo?.ipAddressList &&
        teamMemberInfo?.ipAddressList.length > 0
          ? IconNames.Security
          : undefined,
      iconColor: IconColors.Success,
      iconSize: 'small',
    },
  ];

  function getClientDetails(): IClientDetail {
    const clientDetail: IClientDetail = {
      clientListProperties: [
        {
          key: t('addEditTeamMember.form.roleLabel'),
          value: clientInfo.role,
        },
        {
          key: t('addEditTeamMember.form.clientPermissionGroupLabel'),
          value: clientInfo.permissionGroupId ?? '',
        },
      ],
      id: 1,
      clientName: clientInfo.client,
      isAdmin:
        clientInfo.roleId == UserRoleIds.ClientAdmin ||
        clientInfo.roleId == UserRoleIds.SystemAdmin,
      createdBy: 'adminUser',
      createdDate: new Date('2023-01-15T10:30:00Z'),
      updatedBy: 'adminUser',
      updatedDate: new Date('2023-08-10T15:45:00Z'),
      isExpanded: true,
    };
    return clientDetail;
  }

  const deleteUserRoleForEdit = async () => {
    setIsAddingUser(true);
    try {
      const result = await getUserRoles(teamMemberInfo.userId as string);
      if (result) {
        const userRole = result.data.data.value.userRoles.filter(
          (x: UserRolesData) => x.client.clientId === clientInfo.clientId
        )[0];
        if (userRole)
          await deleteUserRole(userRole.userId, userRole.userRoleId, {
            clientId: clientInfo?.clientId ?? '',
          });
      }
    } catch (e) {
      console.error(e);
    }
    setIsAddingUser(false);
  };

  const toggleItem = (id: number) => {
    console.log(id, clientInfo);
  };

  const onClientDelete = () => {
    setShowDeleteModal(true);
  };

  const onClientEdit = async () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.editClient'),
        type: GA_CLICK_TYPES.ICON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.users.editClient'),
      },
      subPage: {
        subpage_name: t('gaTracking.users.review'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    if (activeStep === 3) await deleteUserRoleForEdit();
    setActiveStep(1);
  };

  //Remove when Consilio User ID is handled elsewhere...
  function uuidv4() {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
      (
        +c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
      ).toString(16)
    );
  }

  const handlePopupConfirm = () => {
    dispatch(setShowModal(false));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.stayHere'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.global.stay'),
      },
      subPage: {
        subpage_name: t('gaTracking.global.unsavedChanges'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const handlePopupCancel = () => {
    const link: string = localStorage?.getItem('currentLink') || '';
    navigate(link, {state: {source: 'nav-panel'}});
    localStorage?.setItem('currentPath', '');
    dispatch(setShowModal(false));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.yesLeaveIt'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.global.leave'),
      },
      subPage: {
        subpage_name: t('gaTracking.global.unsavedChanges'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const closeModalHandler = () => {
    setShowErrorModal(false);
  };

  const handleAddNewMember = async () => {
    setIsAddingUser(true);
    const dataPacket: any = {
      consilioUserId: uuidv4(),
      lastName: teamMemberInfo.lastName,
      firstName: teamMemberInfo.firstName,
      email: teamMemberInfo.email,
      status: 'Active',
      isLocked: false,
      isBillable: teamMemberInfo.isBillable,
      roleId: clientInfo.roleId,
      ipAddressList: teamMemberInfo.ipAddressList,
    };
    if (clientInfo.clientId !== '') dataPacket.clientId = clientInfo.clientId;
    if (clientInfo.permissionGroupId !== '')
      dataPacket.permissionGroupId = clientInfo.permissionGroupId;
    if (projectList) {
      dataPacket.projects = projectList.map((pd) => {
        const projectPermissions = pd.projectPermissions
          ? [...pd.projectPermissions]
          : [];
        if (pd.permissionCodeBudgetConsumption === true) {
          projectPermissions.push(
            getPermissionDetail(
              BUDGET_ESTIMATED.REPORT_BUDGET_CONSUMPTION,
              permissionsFromDB
            ) as never
          );
        }
        if (pd.permissionCodeBudgetAlert === true) {
          projectPermissions.push(
            getPermissionDetail(
              BUDGET_ESTIMATED.REPORT_BUDGET_ALERT,
              permissionsFromDB
            ) as never
          );
        }
        if (pd.manageProjectEvidence) {
          projectPermissions.push(
            getPermissionDetail(
              EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS,
              permissionsFromDB
            ) as never
          );
        }

        if (pd.uploadRights) {
          projectPermissions.push(
            getPermissionDetail(
              EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS,
              permissionsFromDB
            ) as never
          );
        }

        if (pd.initiateProcessingRights) {
          projectPermissions.push(
            getPermissionDetail(
              EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS,
              permissionsFromDB
            ) as never
          );
        }
        return {
          id: pd.projectId,
          name: pd.projectName,
          workspaces: pd.workspaces.map((w) => {
            return {id: w.workspaceId, name: w.workspaceName};
          }),
          permissionGroupId: pd.permissionGroupId ? pd.permissionGroupId : null,
          projectPermissions,
        };
      });
    } else {
      dataPacket.projects = [];
    }

    try {
      const response = await createUser(dataPacket);
      if (response.status === 403) {
        navigate('/unauthorized');
        return;
      }
      if (response.data?.data != null) {
        const userDetail = response.data.data;
        dispatch(setUserId(userDetail.userId));
        setTimeStamp(
          getUTCDateTimeForDisplay(
            userDetail.createdTime,
            ONLY_DATE_SHOW_FORMAT
          )
        );
        showToast(
          t('userHomePage.newUserAdded'),
          'success',
          undefined,
          'center'
        );
      }
      localStorage?.setItem('currentPath', '');
      dispatch(setShowModal(false));
      setActiveStep(activeStep + 1);
    } catch (e) {
      console.error(e);
      setShowErrorModal(true);
      showToast(
        t('userHomePage.failedError'),
        'error',
        undefined,
        'center',
        'bottom',
        {
          code: GA_ERROR_CODES.INTERNAL_SERVER_ERR_500,
          type: GA_ERROR_TYPES.FILE_UPLOAD_ERROR,
        }
      );
    }
    setIsAddingUser(false);
  };

  const handleDeletePopupConfirm = async () => {
    if (activeStep === 3) await deleteUserRoleForEdit();

    dispatch(
      setClientInfo({
        role: '',
        client: '',
        permissionGroup: '',
        permissionGroupId: '',
        project: '',
        workspaces: [],
      })
    );
    dispatch(setProjectList([]));
    setActiveStep(1);
  };

  const handleDeletePopupCancel = () => {
    setShowDeleteModal(false);
  };

  return (
    <div className="user-form">
      <UserDetailsView
        userId={user?.userId ?? ''}
        userFirstName={userFirstName}
        userLastName={userLastName}
        setUserFirstName={setUserFirstName}
        setUserLastName={setUserLastName}
        userEmail={userEmail}
        userListDisabled={userListDisabled}
        setUserListDisabled={setUserListDisabled}
        labelDetails={tempLabelDetails}
        setActiveStep={(step: number) => {
          setActiveStep(step);
        }}
        flowName={ADD_NEW_TEAM_MEMBER}
        activeStep={activeStep}
        createdByName={userFullName}
        createdByDate={timeStamp}
      />
      {clientInfo.roleId === UserRoleIds.SystemAdmin ? (
        <AurSysAdminLabel />
      ) : (
        <AurAccordionGroup
          title={t('editDetails.clientList')}
          fullWidth
          disabled={userListDisabled}
        >
          <AurClientListView
            clientDetail={clientDetails}
            toggleItem={toggleItem}
            setActiveStep={setActiveStep}
            flowName={ADD_NEW_TEAM_MEMBER}
            projects={projectList}
            activeStep={activeStep}
            onEdit={onClientEdit}
            onDelete={onClientDelete}
          />
        </AurAccordionGroup>
      )}
      {activeStep == 2 ? (
        <Box className="everything-button">
          <Button
            className="button-continue typo-text buttonLarge_size submit-btn team-member-save-button"
            type="submit"
            disabled={isAddingUser}
            onClick={() => {
              handleAddNewMember();
            }}
          >
            {t('addEditTeamMember.form.everythingButton')}
            <AurIcon icon={IconNames.ChevronRight} />
          </Button>
        </Box>
      ) : (
        <Box className="last-step-buttons">
          <AurButton
            type="secondary"
            label={t('addEditTeamMember.form.backToManageTeamMembers')}
            size="Large"
            dataTestId="back-button"
            onClick={() => {
              props.resetForm();
              navigate('/users');
            }}
          />
          <Button
            className="button-continue typo-text buttonLarge_size submit-btn"
            type="submit"
            data-testid="add-team-member-button"
            onClick={props.resetForm}
          >
            <img
              alt="plus"
              className="plus-icon"
              src="/assests/images/Vector_plus.svg"
            />
            {t('addEditTeamMember.form.addTeamMember')}
          </Button>
        </Box>
      )}

      <AurPopup
        aria-label={t('userHomePage.modal.errorAddTeamMemberPopup.title')}
        aria-description={t(
          'userHomePage.modal.errorAddTeamMemberPopup.description'
        )}
        className="inactivity-popup-modal error-modal"
        open={showErrorModal}
        onOk={handleAddNewMember}
        onCancel={closeModalHandler}
        popupHeading={t('userHomePage.modal.errorAddTeamMemberPopup.title')}
        showCloseIcon={false}
        showPopupTitle={true}
        showPopupHeading={true}
        popupContent={t(
          'userHomePage.modal.errorAddTeamMemberPopup.description'
        )}
        labelActionCancel={t(
          'userHomePage.modal.errorAddTeamMemberPopup.cancelButtonLabel'
        )}
        labelActionOk={t(
          'userHomePage.modal.errorAddTeamMemberPopup.okButtonLabel'
        )}
        headingIcon={
          <AurIcon
            icon={IconNames.Error}
            color={IconColors.Critical}
            customClassName="heading-warning-icon"
          />
        }
      />

      <AurPopup
        aria-label={t('userHomePage.modal.closePanelUnsavedChangePopup.title')}
        aria-description={t(
          'userHomePage.modal.closePanelUnsavedChangePopup.description',
          {name: `${teamMemberInfo.firstName} ${teamMemberInfo.lastName}`}
        )}
        className="inactivity-popup-modal"
        open={showModal}
        onOk={handlePopupConfirm}
        onCancel={handlePopupCancel}
        popupHeading={t(
          'userHomePage.modal.closePanelUnsavedChangePopup.title'
        )}
        showCloseIcon={false}
        showPopupTitle={true}
        showPopupHeading={true}
        popupTitle={t(
          'userHomePage.modal.closePanelUnsavedChangePopup.heading'
        )}
        popupContent={t(
          'userHomePage.modal.closePanelUnsavedChangePopup.description',
          {name: `${teamMemberInfo.firstName} ${teamMemberInfo.lastName}`}
        )}
        labelActionCancel={t(
          'userHomePage.modal.closePanelUnsavedChangePopup.cancelButtonLabel'
        )}
        labelActionOk={t(
          'userHomePage.modal.closePanelUnsavedChangePopup.okButtonLabel'
        )}
      />

      <AurDialog
        aria-label={t('addEditTeamMember.deleteClientModal.title')}
        aria-description={t('addEditTeamMember.deleteClientModal.description')}
        showDialog={showDeleteModal}
        onOk={handleDeletePopupConfirm}
        onCancel={handleDeletePopupCancel}
        popupTitle={t('addEditTeamMember.deleteClientModal.title')}
        popupContent={t('addEditTeamMember.deleteClientModal.content')}
        labelActionCancel={t(
          'addEditTeamMember.deleteClientModal.cancelButtonLabel'
        )}
        labelActionOk={t('addEditTeamMember.deleteClientModal.okButtonLabel')}
        buttonStyle="sideBySide"
      />
      <AurLoadingIndicator overlay={true} isOpen={isAddingUser} />
    </div>
  );
};

export default Review;
